
import { onMounted, reactive, ref, computed, nextTick } from "vue";
import XAtable from "@/components/XATable.vue";
import XDialog from "@/components/XDialog.vue";
import { sysBmQueryApplyList, sysBmApplySave, sysUserAdsQueryList } from "@/api/api";
import { message } from "ant-design-vue";
import type { FormInstance } from "ant-design-vue";
import type { Dayjs } from "dayjs";
import { hasPermission } from "@/utils/permission";

type RangeValue = [Dayjs, Dayjs];

interface SearchFormState {
  adsId: string;
  bmId: string;
  userId: string;
  createBy?: string | number;
  applyStatus: number | string | undefined;
  time: RangeValue | null;
  pageNo: number;
  pageSize: number;
}

interface FormState {
  id: number | string;
  adsId: string;
  bmId: string;
  bmName: string;
  applyStatus: number;
  remark: string;
}

export default {
  name: "BmManage",
  components: {
    XAtable,
    XDialog,
  },
  setup() {
    const searchFormState = reactive<SearchFormState>({
      adsId: "",
      bmId: "",
      userId: "",
      createBy: undefined,
      applyStatus: "",
      time: null,
      pageNo: 1,
      pageSize: 10,
    });

    const pageNo = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    const tableData = ref<any[]>([]);
    const searchFormRef = ref<FormInstance>();
    const dateFormat = ref("YYYY-MM-DD");

    const dialogVisible = ref(false);
    const auditLoading = ref(false);
    const formRef = ref<FormInstance>();

    const formState = reactive<FormState>({
      id: "",
      adsId: "",
      bmId: "",
      bmName: "",
      applyStatus: 1,
      remark: "",
    });

    interface CreatorOption {
      id: string | number;
      name: string;
    }

    const CREATOR_FILTER_PAGE_SIZE = 50;
    const creatorFilterList = ref<CreatorOption[]>([]);
    const creatorFilterLoading = ref(false);
    const creatorFilterPageNo = ref(1);
    const creatorFilterTotal = ref(0);
    const creatorFilterKeyword = ref("");

    const getCreatorFilterList = (reset = false) => {
      if (reset) {
        creatorFilterPageNo.value = 1;
        creatorFilterList.value = [];
      }

      creatorFilterLoading.value = true;
      const isNumeric = /^\d+$/.test(creatorFilterKeyword.value);
      const params: Record<string, string | number> = {
        pageNo: creatorFilterPageNo.value,
        pageSize: CREATOR_FILTER_PAGE_SIZE,
      };
      if (creatorFilterKeyword.value) {
        if (isNumeric) {
          params.id = creatorFilterKeyword.value;
        } else {
          params.name = creatorFilterKeyword.value;
        }
      }

      sysUserAdsQueryList(params)
        .then((res) => {
          creatorFilterLoading.value = false;
          if (res.code === "200" && res.data) {
            creatorFilterTotal.value = res.data.total ?? 0;
            const raw = res.data.records || [];
            const records: CreatorOption[] = raw.map(
              (row: Record<string, unknown>) => ({
                id: (row.id ?? row.userId ?? row.createBy) as string | number,
                name: String(
                  row.name ?? row.userName ?? row.createByName ?? ""
                ),
              })
            );
            if (reset) {
              creatorFilterList.value = records;
            } else {
              creatorFilterList.value = [
                ...creatorFilterList.value,
                ...records,
              ];
            }
          }
        })
        .catch(() => {
          creatorFilterLoading.value = false;
        });
    };

    const handleCreatorFilterSearch = (value: string) => {
      creatorFilterKeyword.value = value || "";
      getCreatorFilterList(true);
    };

    const handleCreatorFilterScroll = (e: any) => {
      const { target } = e;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
        if (
          creatorFilterList.value.length < creatorFilterTotal.value &&
          !creatorFilterLoading.value
        ) {
          creatorFilterPageNo.value += 1;
          getCreatorFilterList(false);
        }
      }
    };

    const handleCreatorFilterFocus = () => {
      if (creatorFilterList.value.length === 0) {
        creatorFilterKeyword.value = "";
        getCreatorFilterList(true);
      }
    };

    const handleCreatorFilterClear = () => {
      creatorFilterKeyword.value = "";
      getCreatorFilterList(true);
    };

    const baseColumns = [
      {
        title: "ID",
        key: "id",
        dataIndex: "id",
        width: 80,
      },
      {
        title: "广告账户ID",
        key: "adsId",
        dataIndex: "adsId",
        width: 160,
      },
      {
        title: "BMID",
        key: "bmId",
        dataIndex: "bmId",
        width: 160,
      },
      {
        title: "母BM",
        key: "bmBase",
        dataIndex: "bmBase",
        width: 160,
      },
      {
        title: "指纹ID",
        key: "fingerId",
        dataIndex: "fingerId",
        width: 160,
      },
      {
        title: "类型",
        key: "delFlag",
        dataIndex: "delFlag",
        width: 100,
        slots: {
          customRender: "delFlag",
        },
      },
      {
        title: "用户ID",
        key: "userInfo",
        dataIndex: "userInfo",
        width: 160,
        slots: {
          customRender: "userInfo",
        },
      },
      {
        title: "创建者(户)",
        key: "createBy",
        dataIndex: "createBy",
        width: 120,
        slots: {
          customRender: "createBy",
        },
      },
      {
        title: "审核状态",
        key: "applyStatus",
        dataIndex: "applyStatus",
        width: 120,
        slots: {
          customRender: "applyStatus",
        },
      },
      {
        title: "创建时间",
        key: "createTime",
        dataIndex: "createTime",
        width: 180,
      },
      {
        title: "更新时间",
        key: "updateTime",
        dataIndex: "updateTime",
        width: 180,
      },
    ];

    const operationColumn = {
      title: "操作",
      key: "control",
      dataIndex: "control",
      width: 120,
      fixed: "right",
      slots: {
        customRender: "control",
      },
    };

    const hasAnyOperationPermission = computed(() => {
      return hasPermission("sysBm.list.add");
    });

    const columns = computed(() => {
      const cols = [...baseColumns];
      if (hasAnyOperationPermission.value) {
        cols.push(operationColumn);
      }
      return cols;
    });

    const buildQueryParams = () => {
      const params: any = {
        pageNo: searchFormState.pageNo,
        pageSize: searchFormState.pageSize,
      };
      if (searchFormState.adsId) {
        params.adsId = searchFormState.adsId;
      }
      if (searchFormState.bmId) {
        params.bmId = searchFormState.bmId;
      }
      if (searchFormState.userId) {
        params.userId = searchFormState.userId;
      }
      if (
        searchFormState.createBy !== undefined &&
        searchFormState.createBy !== null &&
        searchFormState.createBy !== ""
      ) {
        params.createBy = searchFormState.createBy;
      }
      if (
        searchFormState.applyStatus !== undefined &&
        searchFormState.applyStatus !== ""
      ) {
        params.applyStatus = searchFormState.applyStatus;
      }
      if (searchFormState.time && searchFormState.time.length === 2) {
        const [begin, end] = searchFormState.time;
        const beginStr =
          typeof begin === "string" ? begin : begin.format(dateFormat.value);
        const endStr =
          typeof end === "string" ? end : end.format(dateFormat.value);
        params.beginTime = beginStr + " 00:00:00";
        params.endTime = endStr + " 23:59:59";
      }
      return params;
    };

    const getData = () => {
      loading.value = true;
      const params = buildQueryParams();
      sysBmQueryApplyList(params)
        .then((res) => {
          loading.value = false;
          if (res.code === "200") {
            total.value = res.data.total || 0;
            tableData.value = res.data.records || [];
          }
        })
        .catch(() => {
          loading.value = false;
        });
    };

    const search = () => {
      searchFormState.pageNo = 1;
      pageNo.value = 1;
      getData();
    };

    const resetForm = () => {
      searchFormState.adsId = "";
      searchFormState.bmId = "";
      searchFormState.userId = "";
      searchFormState.createBy = undefined;
      searchFormState.applyStatus = "";
      searchFormState.time = null;
      searchFormState.pageNo = 1;
      searchFormState.pageSize = 10;
      pageNo.value = 1;
      pageSize.value = 10;
      creatorFilterKeyword.value = "";
      creatorFilterPageNo.value = 1;
      creatorFilterList.value = [];
      creatorFilterTotal.value = 0;
      searchFormRef.value?.resetFields();
      getData();
    };

    const pageNoChange = (val: number) => {
      searchFormState.pageNo = val;
      pageNo.value = val;
      getData();
    };

    const onShowSizeChange = (current: number, size: number) => {
      searchFormState.pageSize = size;
      pageSize.value = size;
      searchFormState.pageNo = 1;
      pageNo.value = 1;
      getData();
    };

    const auditClick = async (row: any) => {
      dialogVisible.value = true;
      await nextTick();

      formState.id = row.id || "";
      formState.adsId = row.adsId || "";
      formState.bmId = row.bmId || "";
      formState.bmName = row.bmName || "";
      formState.applyStatus = 1;
      formState.remark = "";

      await nextTick();
      formRef.value?.clearValidate();
    };

    const handleCancel = async () => {
      dialogVisible.value = false;
      auditLoading.value = false;
      await nextTick();
      formRef.value?.resetFields();
    };

    const confirm = async () => {
      try {
        await formRef.value?.validateFields();
        auditLoading.value = true;
        sysBmApplySave({
          id: formState.id,
          applyStatus: formState.applyStatus,
          remark: formState.remark,
        })
          .then((res) => {
            auditLoading.value = false;
            if (res.code === "200") {
              message.success("审核成功");
              dialogVisible.value = false;
              getData();
            }
          })
          .catch(() => {
            auditLoading.value = false;
          });
      } catch (error) {
        auditLoading.value = false;
        // eslint-disable-next-line no-console
        console.log("Failed:", error);
      }
    };

    onMounted(() => {
      getData();
    });

    return {
      searchFormState,
      searchFormRef,
      columns,
      tableData,
      pageNo,
      pageSize,
      total,
      loading,
      dateFormat,
      search,
      resetForm,
      pageNoChange,
      onShowSizeChange,
      dialogVisible,
      auditLoading,
      formRef,
      formState,
      auditClick,
      handleCancel,
      confirm,
      creatorFilterList,
      creatorFilterLoading,
      handleCreatorFilterSearch,
      handleCreatorFilterScroll,
      handleCreatorFilterFocus,
      handleCreatorFilterClear,
    };
  },
};
